import classNames from "classnames";
import { camelizeKeys } from "humps";
import filter from "lodash/filter";
import find from "lodash/find";
import map from "lodash/map";
import { KPIsReviewSidepanelData } from "PFApp/components/kpis_review_sidepanel/kpis_review_sidepanel.types";
import { SearchHighlights } from "PFApp/components/search_highlight";
import { TemplateKey } from "PFApp/constants/templates";
import { useTableSorting } from "PFApp/hooks/table";
import { WorkflowTab } from "PFApp/workflow/workflow_store";
import Table from "PFComponents/table";
import { ColumnHelper, PaginationOptions } from "PFComponents/table/table.types";
import { TableColumn } from "PFTypes";
import { Id, MetaWithPagination, WorkflowActivity } from "PFTypes";
import { CSSProperties, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { NoItems } from "../no_items";
import css from "./activities_table.module.scss";
import { RowActionsCell } from "./cells";
import { getColumnConfig } from "./get_column_config";
import { useCellFactory } from "./use_cell_factory";

type ActivitiesTableProps = {
  type: TemplateKey.Role | TemplateKey.Engagement | TemplateKey.AuditRole;
  activities: WorkflowActivity[];
  columns: TableColumn[];
  meta?: MetaWithPagination;
  isLoading?: boolean;
  selectedIds?: Record<string, true>;
  pinnedIds?: number[];
  setPinnedIds?: (ids: number[]) => void;
  setSelectedIds?: (selectedIds: Record<string, true>) => void;
  onMarkAsRead?: (activityId: Id) => Promise<void>;
  onPageChange?: (page: number) => void;
  onShareActivity?: (activityId: Id) => void;
  onRefetch?: VoidFunction;
  onRemoveSuccess?: VoidFunction;
  onKPIsReviewClick?: (data: KPIsReviewSidepanelData) => void;
  onOrderChange?: any;
  classes?: {
    pagination?: string;
    table?: string;
  };
  styles?: {
    pagination?: CSSProperties;
  };
};

export const ActivitiesTable = ({
  type,
  activities,
  columns,
  meta,
  isLoading,
  selectedIds,
  pinnedIds,
  setPinnedIds,
  setSelectedIds,
  onMarkAsRead,
  onShareActivity,
  onPageChange,
  onRefetch,
  onRemoveSuccess,
  onKPIsReviewClick,
  onOrderChange,
  classes,
  styles
}: ActivitiesTableProps) => {
  const { t } = useTranslation("workflow");
  const renderCell = useCellFactory(type);

  const paginationOptions: PaginationOptions | undefined = meta && {
    currentPage: meta.page,
    totalPages: meta.totalPages,
    onClick: onPageChange,
    className: classNames(css.stickyPagination, classes?.pagination),
    style: styles?.pagination
  };

  const { sorting, handleSortingChange } = useTableSorting({
    metaOrder: meta?.order,
    setParamsOrder: (arg) => onOrderChange(arg),
    parseMetaOrderName: (orderKey) => filter(columns, { orderKey }).map(({ name }) => name) || orderKey,
    parseTableSortingId: (name) => find(columns, { name })?.orderKey || name
  });

  const hasAnyHighlights = activities.some((activity) => !!activity.optional_fields?.highlights);
  const hasSelection = !!selectedIds && Object.keys(selectedIds).length > 0;

  const renderExpandedRow = ({ optional_fields: optionalFields }: WorkflowActivity) => {
    const { highlights } = optionalFields || {};
    return highlights ? <SearchHighlights highlights={camelizeKeys(highlights)} alwaysExpanded /> : null;
  };

  const generateColumns = useCallback(
    (columnHelper: ColumnHelper<WorkflowActivity>) => [
      ...map(columns, (column) =>
        columnHelper.accessor(({ activity }) => activity[column.name], {
          ...getColumnConfig(column, meta?.order?.options || []),
          cell: renderCell(column)
        })
      ),
      columnHelper.accessor(({ activity }) => activity, {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ getValue }) => (
          <RowActionsCell
            item={getValue()}
            type={type}
            pinnedIds={pinnedIds}
            onMarkAsRead={onMarkAsRead}
            setPinnedIds={setPinnedIds}
            onShare={onShareActivity}
            onRefetch={onRefetch}
            onRemoveSuccess={onRemoveSuccess}
            onKPIsReviewClick={onKPIsReviewClick}
            disabled={hasSelection}
          />
        )
      })
    ],
    [
      columns,
      meta?.order?.options,
      renderCell,
      type,
      pinnedIds,
      onMarkAsRead,
      setPinnedIds,
      onShareActivity,
      onRefetch,
      onRemoveSuccess,
      onKPIsReviewClick,
      hasSelection
    ]
  );

  if (activities.length === 0) {
    return <NoItems itemType={type === TemplateKey.Role ? WorkflowTab.Roles : WorkflowTab.Engagements} />;
  }

  return (
    <div role="region" aria-label={t("activitiesTable")} data-qa-id="WorkflowPage.activitiesTable">
      <Table<WorkflowActivity>
        className={classNames(css.activitiesTable, classes?.table)}
        isLoading={isLoading}
        tableData={activities}
        generateColumns={generateColumns}
        paginationOptions={paginationOptions}
        disableAutoResizing
        disableInternalScrolling
        enableRowSelection={!!setSelectedIds}
        enableMultiRowSelection
        onRowSelectionChange={setSelectedIds}
        rowSelection={selectedIds}
        getRowId={({ id }) => String(id)}
        onSortingChange={handleSortingChange}
        controlledSorting={sorting}
        enableSorting={!!meta?.order}
        enableRowExpansion
        renderExpandedRow={renderExpandedRow}
        expandedRows={hasAnyHighlights || {}}
        rowSelectionConditions={{
          isRowExpandable: ({ original }) => !!original.optional_fields?.highlights
        }}
      />
    </div>
  );
};
